const FIELD_TYPES = ["text", "number", "enum"] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export function isFieldType(type: string): type is FieldType {
  return FIELD_TYPES.some((t) => t === type);
}

export interface Field {
  key: string;
  label: string;
  type: FieldType;
  values?: string[];
}

export interface Filter {
  id: string;
  field: string;
  type: FieldType;
  opId: string;
  value: string | number;
  value2?: number;
}

export interface Operator {
  id: string;
  label: string;
  types: FieldType[];
  test: (cell: unknown, value: string | number, value2?: number) => boolean;
  sql: (field: string, value: string | number, value2?: number) => string;
}

function escStr(v: string | number): string {
  return String(v).replace(/'/g, "''");
}

export const OPERATORS: Operator[] = [
  {
    id: "contains",
    label: "contains",
    types: ["text"],
    test: (cell, v) =>
      String(cell ?? "")
        .toLowerCase()
        .includes(String(v).toLowerCase()),
    sql: (f, v) => `"${f}" ILIKE '%${escStr(v)}%'`,
  },
  {
    id: "is",
    label: "is",
    types: ["text", "enum"],
    test: (cell, v) =>
      String(cell ?? "").toLowerCase() === String(v).toLowerCase(),
    sql: (f, v) => `"${f}" = '${escStr(v)}'`,
  },
  {
    id: "is_not",
    label: "is not",
    types: ["text", "enum"],
    test: (cell, v) =>
      String(cell ?? "").toLowerCase() !== String(v).toLowerCase(),
    sql: (f, v) => `"${f}" != '${escStr(v)}'`,
  },
  {
    id: "gt",
    label: ">",
    types: ["number"],
    test: (cell, v) => Number(cell) > Number(v),
    sql: (f, v) => `"${f}" > ${Number(v)}`,
  },
  {
    id: "lt",
    label: "<",
    types: ["number"],
    test: (cell, v) => Number(cell) < Number(v),
    sql: (f, v) => `"${f}" < ${Number(v)}`,
  },
  {
    id: "gte",
    label: "≥",
    types: ["number"],
    test: (cell, v) => Number(cell) >= Number(v),
    sql: (f, v) => `"${f}" >= ${Number(v)}`,
  },
  {
    id: "lte",
    label: "≤",
    types: ["number"],
    test: (cell, v) => Number(cell) <= Number(v),
    sql: (f, v) => `"${f}" <= ${Number(v)}`,
  },
  {
    id: "eq",
    label: "=",
    types: ["number"],
    test: (cell, v) => Number(cell) === Number(v),
    sql: (f, v) => `"${f}" = ${Number(v)}`,
  },
  {
    id: "between",
    label: "between",
    types: ["number"],
    test: (cell, v, v2) =>
      Number(cell) >= Number(v) && Number(cell) <= Number(v2 ?? v),
    sql: (f, v, v2) => `"${f}" BETWEEN ${Number(v)} AND ${Number(v2 ?? v)}`,
  },
];

export function getOpsForType(type: FieldType): Operator[] {
  return OPERATORS.filter((op) => op.types.includes(type));
}

export function getOp(id: string): Operator | undefined {
  return OPERATORS.find((op) => op.id === id);
}
